import type { KeyboardEvent as ReactKeyboardEvent, ReactNode, SetStateAction } from 'react'
import { useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { HourlyDataTable } from 'ui/components/HourlyDataTable/HourlyDataTable'
import { getCellDisplay } from 'ui/components/HourlyDataTable/HourlyDataTable.cellEdit.helper'
import hourlyStyles from 'ui/components/HourlyDataTable/HourlyDataTable.module.less'
import type { MouseEventLike, RowValues, TableCellEditRule } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { HourlyInputCell } from 'ui/components/HourlyDataTable/HourlyInputCell'
import { useTableCellCopy } from 'ui/components/HourlyDataTable/hooks/useTableCellClipboard'
import { useTableCellEdit } from 'ui/components/HourlyDataTable/hooks/useTableCellEdit'
import { useTableCellNavigation } from 'ui/components/HourlyDataTable/hooks/useTableCellNavigation'
import { useTableCellSelection } from 'ui/components/HourlyDataTable/hooks/useTableCellSelection'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { createElplanEditorAdapter, getElplanRowCellKey, parseElplanCellKey } from './cells.adapter'
import { groupElplanDraftByHour } from './draft'
import styles from './ElplanEditor.module.less'
import type {
  ElplanDraft,
  ElplanEditorRow,
  ElplanRowRead,
  ElplanSlotEdit,
  ElplanSlotFaults,
  ElplanSlotUnit,
  ElplanSummaryTextEntry,
} from './ElplanEditor.types'
import { ElplanHourSummaryCell } from './ElplanHourSummaryCell'
import { buildElplanEditorRows, getElplanRowClassName, readElplanRow } from './rows'
import { clearRowCells, toSlotEditsOfRowWrite } from './rowWrites'
import { useElplanEditorPaste } from './useElplanEditorPaste'
import { isElplanVolumeCharacter } from './volume'

const ELPLAN_VOLUME_RULE: TableCellEditRule = {
  kind: 'number',
  admitsSign: true,
  acceptsCharacter: isElplanVolumeCharacter,
}

const ELPLAN_SUMMARY_RULE: TableCellEditRule = { kind: 'number', admitsSign: false }

function getHourToggleIcon(isExpanded: boolean): string {
  return isExpanded ? 'fal fa-chevron-down' : 'fal fa-chevron-right'
}

type ElplanEditorTableProps = {
  draft: ElplanDraft
  mode: ElplanSlotUnit
  expandedHourStarts: ReadonlySet<ISODateTime>
  faults: ElplanSlotFaults
  changedSlotStarts: ReadonlySet<ISODateTime>
  onEdits: (edits: ElplanSlotEdit[]) => void
  onTypedEdits: (edits: ElplanSlotEdit[]) => void
  onTypingEnd: () => void
  onToggleHour: (hourStart: ISODateTime) => void
  onSetAllHoursExpanded: (isExpanded: boolean) => void
}

export function ElplanEditorTable({
  draft,
  mode,
  expandedHourStarts,
  faults,
  changedSlotStarts,
  onEdits,
  onTypedEdits,
  onTypingEnd,
  onToggleHour,
  onSetAllHoursExpanded,
}: ElplanEditorTableProps): ReactNode {
  const { t } = useTranslation()
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const inputRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({})
  const rows = buildElplanEditorRows(draft, mode, expandedHourStarts)
  const areAllHoursExpanded = expandedHourStarts.size === groupElplanDraftByHour(draft).length
  const expandAllLabel = areAllHoursExpanded ? t('Collapse all quarters') : t('Expand all quarters')
  const adapter = createElplanEditorAdapter(rows)
  const {
    selectedCellKeys,
    selectedCellKeysRef,
    selectedCellKeysForClipboardRef,
    bulkReplaceOnNextInputRef,
    setAnchorCellKey,
    replaceSelection,
    clearSelection,
    handleCellMouseDown,
    handleCellMouseOver,
    getEditTargets: getSelectionEditTargets,
    takeReplaceOnType,
  } = useTableCellSelection({ adapter, tableContainerRef })

  const valuesByRowRef = useRef<RowValues[]>([])
  const rowReads = rows.map((row) => readElplanRow(row, mode, draft, faults, changedSlotStarts))
  const valuesByRow = rows.map((row, rowIndex) => ({ [getElplanRowCellKey(row)]: rowReads[rowIndex].valueText }))
  useLayoutEffect(() => {
    valuesByRowRef.current = valuesByRow
  })

  function isCellEditable(cellKey: string): boolean {
    return parseElplanCellKey(cellKey)?.unit === mode
  }

  function writeValuesByRow(action: SetStateAction<RowValues[]>, writeEdits: (edits: ElplanSlotEdit[]) => void): void {
    const nextValuesByRow = typeof action === 'function' ? action(valuesByRow) : action
    const edits = toSlotEditsOfRowWrite(valuesByRow, nextValuesByRow, draft)
    if (edits.length > 0) {
      writeEdits(edits)
    }
  }

  function getEditTargets(cellKey: string): ReadonlySet<string> {
    return new Set([...getSelectionEditTargets(cellKey)].filter(isCellEditable))
  }

  const { handleCellKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
    adapter,
    clearSelection,
    clearValuesForSelection: (cellKeys) => writeValuesByRow((rows) => clearRowCells(rows, cellKeys), onEdits),
    replaceSelection,
    setAnchorCellKey,
    inputRefs,
    bulkReplaceOnNextInputRef,
    selectedCellKeysRef,
    isCellEditable,
  })

  const { handleCellValueChange, handleCellKeyDown } = useTableCellEdit({
    adapter,
    inputRefs,
    valuesByRow,
    setValuesByRow: (action) => writeValuesByRow(action, onTypedEdits),
    getCellRule: (cellKey) => (isCellEditable(cellKey) ? ELPLAN_VOLUME_RULE : ELPLAN_SUMMARY_RULE),
    getEditTargets,
    takeReplaceOnType,
    handleNavigationKeyDown,
  })

  const hourSummaryReadOnlyMessage = t(
    'Hour rows show the mean of their quarters. Select quarters to edit them, or switch to hour mode to enter one value per hour.'
  )

  function alertIfNothingToWrite(summaryKey: string): void {
    if (getEditTargets(summaryKey).size === 0) {
      alertStore.info(hourSummaryReadOnlyMessage)
    }
  }

  function focusTextEntryTarget(summaryKey: string, entry: ElplanSummaryTextEntry): void {
    const editTargets = getEditTargets(summaryKey)
    const targetKey = rows.map(getElplanRowCellKey).find((rowCellKey) => editTargets.has(rowCellKey))
    if (targetKey === undefined) {
      if (entry === 'character') {
        alertIfNothingToWrite(summaryKey)
      }
      return
    }
    const targetField = inputRefs.current[targetKey]
    targetField?.focus()
    targetField?.select()
  }
  useTableCellCopy({ tableContainerRef, adapter, selectedCellKeysForClipboardRef, valuesByRowRef })
  useElplanEditorPaste({
    tableContainerRef,
    selectedCellKeysRef,
    draft,
    mode,
    onEdits,
    getEditTargets,
    replaceSelection,
    setAnchorCellKey,
    onEmptyPaste: alertIfNothingToWrite,
  })
  function renderRowCell(
    row: ElplanEditorRow,
    { isSummary, displayText, fault, hasEmptyValues, isChanged }: ElplanRowRead
  ): ReactNode {
    const cellKey = getElplanRowCellKey(row)
    const isSelected = selectedCellKeys.has(cellKey)
    const value = getCellDisplay(displayText, ELPLAN_VOLUME_RULE)
    const ariaLabel = t('Volume {{time}}', { time: row.label })
    const emptyValuesIcon = hasEmptyValues ? (
      <InfoBanner variant="icon" style="warning" text={t('{{time}} has empty values', { time: row.label })} />
    ) : null
    const sharedProps = {
      cellKey,
      isSelected,
      isUnsaved: isChanged,
      ariaLabel,
      onKeyDown: (event: ReactKeyboardEvent) => handleCellKeyDown(cellKey, event),
      onMouseDown: (event: MouseEventLike) => handleCellMouseDown(cellKey, event),
      onMouseOver: () => handleCellMouseOver(cellKey),
    }

    if (isSummary && row.kind === 'hour') {
      return (
        <ElplanHourSummaryCell
          key={cellKey}
          {...sharedProps}
          value={value}
          faultMessage={fault ? t('This hour has an invalid quarter') : undefined}
          readOnlyHint={hourSummaryReadOnlyMessage}
          emptyValuesIcon={emptyValuesIcon}
          registerRef={(element) => {
            inputRefs.current[cellKey] = element
          }}
          onTextEntry={(entry) => focusTextEntryTarget(cellKey, entry)}
          onClearKey={() => alertIfNothingToWrite(cellKey)}
        />
      )
    }
    return (
      <HourlyInputCell
        key={cellKey}
        {...sharedProps}
        value={value}
        isInvalid={fault !== undefined}
        faultMessage={
          fault === 'too-many-decimals'
            ? t('Enter a value with at most one decimal')
            : fault
              ? t('Enter a number')
              : undefined
        }
        isGroupStart
        isGroupEnd
        leadingAdornment={emptyValuesIcon}
        onValueChange={(editedText) => handleCellValueChange(cellKey, editedText)}
        onFocus={() => {}}
        onBlur={onTypingEnd}
        registerRef={(element: HTMLInputElement | null) => {
          inputRefs.current[cellKey] = element
        }}
      />
    )
  }
  return (
    <HourlyDataTable
      rowCount={rows.length}
      tableContainerRef={tableContainerRef}
      containerClassName={styles.ElplanEditor_Table}
      isCompact
      leadingColumn={
        mode === 'quarter'
          ? {
              header: (
                <NewButton
                  iconButton
                  icon={getHourToggleIcon(areAllHoursExpanded)}
                  tooltip={{ title: expandAllLabel }}
                  aria-expanded={areAllHoursExpanded}
                  onClick={() => onSetAllHoursExpanded(!areAllHoursExpanded)}
                />
              ),
              renderCell: (rowIndex) => {
                const row = rows[rowIndex]
                if (!row || row.kind !== 'hour') {
                  return null
                }
                return (
                  <NewButton
                    iconButton
                    icon={getHourToggleIcon(row.isExpanded)}
                    tooltip={{
                      title: row.isExpanded
                        ? t('Hide quarters {{time}}', { time: row.label })
                        : t('Show quarters {{time}}', { time: row.label }),
                    }}
                    aria-expanded={row.isExpanded}
                    onClick={() => onToggleHour(row.start)}
                  />
                )
              },
            }
          : undefined
      }
      columnHeaders={
        <th
          className={[
            hourlyStyles.HourlyDataTable_BidTableTh,
            hourlyStyles.HourlyDataTable_BidTableStickyTopPrimary,
            styles.ElplanEditor_VolumeHeader,
          ].join(' ')}
        >
          {t('Volume (MW)')}
        </th>
      }
      getRowLabel={(rowIndex) => rows[rowIndex]?.label ?? ''}
      getRowClassName={(rowIndex) => (rows[rowIndex] ? getElplanRowClassName(rows[rowIndex]) : '')}
      renderCellsForRow={(rowIndex) => (rows[rowIndex] ? renderRowCell(rows[rowIndex], rowReads[rowIndex]) : null)}
    />
  )
}
